import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  BookOpen,
  ArrowRight,
  PlayCircle,
  Sparkles,
  Smartphone,
  Users,
  Award,
  Clock,
  Trophy,
  Brain,
  Wifi,
  Quote,
  GraduationCap,
  Rocket,
  ClipboardCheck,
} from 'lucide-react';
import { Button, Card, CardContent, Badge } from '@nzassa/ui';
import { COLORS } from '@nzassa/shared';
import { BrandHeader } from '@/components/layout/BrandHeader';
import { BrandFooter } from '@/components/layout/BrandFooter';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/* ────────────────────────────────────────────────────────────
   DATA (static — icons, colors, keys)
   ──────────────────────────────────────────────────────────── */

const STAT_ITEMS = [
  { value: '20 000+', labelKey: 'landing.stats.apprenantes', icon: Users, color: COLORS.blue },
  { value: '8', labelKey: 'landing.stats.sessions', icon: BookOpen, color: COLORS.orange },
  { value: '4', labelKey: 'landing.stats.semaines', icon: Clock, color: '#059669' },
  { value: '100%', labelKey: 'landing.stats.gratuit', icon: GraduationCap, color: '#7C3AED' },
];

const FEATURE_ITEMS = [
  { icon: Smartphone, titleKey: 'landing.features.mobile_first', descKey: 'landing.features.mobile_first_desc' },
  { icon: Brain, titleKey: 'landing.features.coach_ia', descKey: 'landing.features.coach_ia_desc' },
  { icon: Trophy, titleKey: 'landing.features.gamification', descKey: 'landing.features.gamification_desc' },
  { icon: Award, titleKey: 'landing.features.certificat', descKey: 'landing.features.certificat_desc' },
  { icon: PlayCircle, titleKey: 'landing.features.micro_lecons', descKey: 'landing.features.micro_lecons_desc' },
  { icon: Wifi, titleKey: 'landing.features.connexion_legere', descKey: 'landing.features.connexion_legere_desc' },
];

const SESSION_ITEMS = [
  { id: 'S1', weekKey: 'landing.parcours.semaine_1', titleKey: 'landing.parcours.s1_title', descKey: 'landing.parcours.s1_desc', phase: 'piscine', color: COLORS.blue },
  { id: 'S2', weekKey: 'landing.parcours.semaine_1', titleKey: 'landing.parcours.s2_title', descKey: 'landing.parcours.s2_desc', phase: 'piscine', color: COLORS.blue },
  { id: 'S3', weekKey: 'landing.parcours.semaine_2', titleKey: 'landing.parcours.s3_title', descKey: 'landing.parcours.s3_desc', phase: 'piscine', color: '#059669' },
  { id: 'S4', weekKey: 'landing.parcours.semaine_2', titleKey: 'landing.parcours.s4_title', descKey: 'landing.parcours.s4_desc', phase: 'piscine', color: '#059669' },
  { id: 'S5', weekKey: 'landing.parcours.semaine_3', titleKey: 'landing.parcours.s5_title', descKey: 'landing.parcours.s5_desc', phase: 'intensif', color: COLORS.orange },
  { id: 'S6', weekKey: 'landing.parcours.semaine_3', titleKey: 'landing.parcours.s6_title', descKey: 'landing.parcours.s6_desc', phase: 'intensif', color: COLORS.orange },
  { id: 'S7', weekKey: 'landing.parcours.semaine_4', titleKey: 'landing.parcours.s7_title', descKey: 'landing.parcours.s7_desc', phase: 'hackathon', color: '#7C3AED' },
  { id: 'S8', weekKey: 'landing.parcours.semaine_4', titleKey: 'landing.parcours.s8_title', descKey: 'landing.parcours.s8_desc', phase: 'hackathon', color: '#7C3AED' },
];

const TESTIMONIAL_ITEMS = [
  { name: 'Aminata K.', roleKey: 'landing.testimonials.aminata_role', quoteKey: 'landing.testimonials.aminata_quote', avatar: '/logos/nzassalearn-compact.svg' },
  { name: 'Fatou D.', roleKey: 'landing.testimonials.fatou_role', quoteKey: 'landing.testimonials.fatou_quote', avatar: '/logos/nzassalearn-compact.svg' },
  { name: 'Marie-Claire T.', roleKey: 'landing.testimonials.marie_role', quoteKey: 'landing.testimonials.marie_quote', avatar: '/logos/nzassalearn-compact.svg' },
];

const TRUST_PARTNERS = [
  { name: 'ANSUT', logo: '/logos/ansut.svg' },
  { name: 'NzassaLearn', logo: '/logos/nzassalearn.svg' },
];

/* ────────────────────────────────────────────────────────────
   HELPERS
   ──────────────────────────────────────────────────────────── */

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5 },
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.08 } },
  viewport: { once: true },
};

/* ────────────────────────────────────────────────────────────
   COMPONENT
   ──────────────────────────────────────────────────────────── */

export function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle();
  const onStart = () => navigate('/connexion');

  return (
    <div className="relative min-h-screen" style={{ background: COLORS.bg }}>
      {/* ── BRAND HEADER ───────────────────────────────────── */}
      <BrandHeader />

      {/* ── HERO ───────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${COLORS.blue} 0%, #0E4D78 50%, #114E7A 100%)`,
        }}
      >
        {/* Decorative shapes */}
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.07]"
          style={{ background: COLORS.orange, transform: 'translate(30%, -40%)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-[0.05]"
          style={{ background: COLORS.white, transform: 'translate(-30%, 40%)' }}
        />

        <div className="relative mx-auto max-w-6xl px-4 py-16 md:py-24 lg:py-28">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            {/* Text content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Badge
                className="mb-6 border-0"
                style={{ background: 'rgba(255,120,0,0.2)', color: COLORS.orange }}
              >
                {t('landing.badge_programme')}
              </Badge>

              <h1 className="text-4xl font-extrabold tracking-tight text-white md:text-5xl lg:text-[3.5rem] leading-[1.15]">
                {t('landing.hero_title_1')}
                <span style={{ color: COLORS.orange }}>{t('landing.hero_title_highlight')}</span>
                {t('landing.hero_title_2')}
              </h1>

              <p
                className="mt-6 max-w-xl text-lg leading-relaxed text-white/70"
                dangerouslySetInnerHTML={{ __html: t('landing.hero_subtitle') }}
              />

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={onStart}
                  className="group"
                  style={{ background: COLORS.orange, borderColor: COLORS.orange }}
                >
                  {t('landing.cta_commencer')}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onStart}
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  {t('landing.cta_decouvrir')}
                </Button>
              </div>

              {/* Stats strip */}
              <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {STAT_ITEMS.map(({ value, labelKey, icon: Icon, color }) => (
                  <div key={labelKey} className="text-center">
                    <div
                      className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl"
                      style={{ background: 'rgba(255,255,255,0.1)' }}
                    >
                      <Icon className="h-5 w-5" style={{ color }} />
                    </div>
                    <div className="text-xl font-bold text-white">{value}</div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-white/50">
                      {t(labelKey)}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Visual mockup card */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="relative hidden lg:block"
            >
              <div className="absolute -top-8 -right-8 h-56 w-56 rounded-full bg-white/5 blur-3xl" />
              <div className="absolute -bottom-8 -left-8 h-56 w-56 rounded-full opacity-10 blur-3xl" style={{ background: COLORS.orange }} />

              <Card className="relative z-10 border-white/10 bg-white/[0.06] backdrop-blur-sm shadow-2xl">
                <CardContent className="p-6">
                  {/* Phone mockup content */}
                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-white/40">
                          {t('landing.mockup.en_cours')}
                        </div>
                        <div className="mt-1 text-base font-bold text-white">
                          {t('landing.mockup.cours_titre')}
                        </div>
                        <div className="mt-1 text-xs text-white/50">{t('landing.mockup.lecon')}</div>
                      </div>
                      <Badge
                        className="rounded-full border-0"
                        style={{ background: 'rgba(255,120,0,0.2)', color: COLORS.orange }}
                      >
                        70%
                      </Badge>
                    </div>
                    <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: '70%', background: COLORS.orange }}
                      />
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10">
                          <Sparkles className="h-4 w-4" style={{ color: COLORS.orange }} />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-white">{t('landing.mockup.coach_ia')}</div>
                          <div className="text-xs text-white/50">{t('landing.mockup.aide_quiz')}</div>
                        </div>
                      </div>
                      <div
                        className="rounded-lg px-3 py-1.5 text-xs font-medium"
                        style={{ background: COLORS.orange, color: COLORS.white }}
                      >
                        {t('landing.mockup.discuter')}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                      <div className="text-xs font-medium text-white/40">{t('landing.mockup.prochaine_etape')}</div>
                      <div className="mt-1 text-sm font-bold text-white">{t('landing.mockup.mini_quiz')}</div>
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-white/50">
                        <ClipboardCheck className="h-3.5 w-3.5" style={{ color: COLORS.orange }} />
                        {t('landing.mockup.questions')}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                      <div className="text-xs font-medium text-white/40">{t('landing.mockup.classement')}</div>
                      <div className="mt-1 text-sm font-bold text-white">{t('landing.mockup.rang')}</div>
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-white/50">
                        <Trophy className="h-3.5 w-3.5" style={{ color: COLORS.orange }} />
                        {t('landing.mockup.points_gain')}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ──────────────────────────────────────── */}
      <section className="border-b border-slate-200/60 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-widest text-slate-400">
            {t('landing.trust.soutenu_par')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {TRUST_PARTNERS.map(({ name, logo }) => (
              <img
                key={name}
                src={logo}
                alt={name}
                className="h-8 md:h-10 opacity-40 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                loading="lazy"
              />
            ))}
            {/* Placeholder institutional logos */}
            <div className="flex h-8 items-center gap-2 rounded-lg border border-dashed border-slate-200 px-4 text-xs text-slate-300">
              <GraduationCap className="h-4 w-4" />
              {t('landing.trust.ministere')}
            </div>
            <div className="flex h-8 items-center gap-2 rounded-lg border border-dashed border-slate-200 px-4 text-xs text-slate-300">
              <Users className="h-4 w-4" />
              {t('landing.trust.partenaires')}
            </div>
          </div>
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/a-propos')}
              className="text-xs font-medium transition-colors hover:underline"
              style={{ color: COLORS.blue }}
            >
              {t('landing.trust.en_savoir_plus')}
            </button>
          </div>
        </div>
      </section>

      {/* ── FEATURES ───────────────────────────────────────── */}
      <section id="features" className="bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-20">
          <motion.div {...fadeUp} className="text-center mb-14">
            <Badge
              className="mb-4 border-0"
              style={{ background: `${COLORS.orange}15`, color: COLORS.orange }}
            >
              {t('landing.features.badge')}
            </Badge>
            <h2 className="text-3xl font-bold md:text-4xl" style={{ color: COLORS.ink }}>
              {t('landing.features.title')}
            </h2>
            <p className="mt-3 mx-auto max-w-lg text-slate-500">
              {t('landing.features.subtitle')}
            </p>
          </motion.div>

          <motion.div
            {...staggerContainer}
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {FEATURE_ITEMS.map(({ icon: Icon, titleKey, descKey }, i) => (
              <motion.div
                key={titleKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
              >
                <Card className="group h-full border-slate-200/80 hover:border-slate-300 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-xl mb-4 transition-colors duration-300 group-hover:scale-105"
                      style={{ background: `${COLORS.orange}12` }}
                    >
                      <Icon className="h-6 w-6" style={{ color: COLORS.orange }} />
                    </div>
                    <h3 className="text-base font-bold mb-1.5" style={{ color: COLORS.ink }}>
                      {t(titleKey)}
                    </h3>
                    <p className="text-sm leading-relaxed text-slate-500">{t(descKey)}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PARCOURS (8 Sessions ASUP Timeline) ────────────── */}
      <section
        id="parcours"
        className="border-y border-slate-200/60"
        style={{ background: COLORS.bg }}
      >
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-20">
          <motion.div {...fadeUp} className="text-center mb-14">
            <Badge
              className="mb-4 border-0"
              style={{ background: `${COLORS.blue}12`, color: COLORS.blue }}
            >
              {t('landing.parcours.badge')}
            </Badge>
            <h2 className="text-3xl font-bold md:text-4xl" style={{ color: COLORS.ink }}>
              {t('landing.parcours.title')}
            </h2>
            <p className="mt-3 mx-auto max-w-lg text-slate-500">
              {t('landing.parcours.subtitle')}
            </p>
          </motion.div>

          {/* Phase labels */}
          <div className="mb-8 flex flex-wrap justify-center gap-3">
            {[
              { labelKey: 'landing.parcours.piscine', color: COLORS.blue, sessions: 'S1-S4' },
              { labelKey: 'landing.parcours.intensif', color: COLORS.orange, sessions: 'S5-S6' },
              { labelKey: 'landing.parcours.hackathon', color: '#7C3AED', sessions: 'S7-S8' },
            ].map(({ labelKey, color, sessions }) => (
              <div
                key={labelKey}
                className="flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold"
                style={{ background: `${color}12`, color }}
              >
                <div className="h-2 w-2 rounded-full" style={{ background: color }} />
                {t(labelKey)} ({sessions})
              </div>
            ))}
          </div>

          {/* Timeline grid */}
          <div className="grid gap-4 sm:grid-cols-2">
            {SESSION_ITEMS.map((session, i) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, x: i % 2 === 0 ? -16 : 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
              >
                <Card
                  className="h-full border-slate-200/80 bg-white hover:shadow-md transition-shadow overflow-hidden"
                >
                  <div className="flex h-full">
                    {/* Colored left border */}
                    <div
                      className="w-1.5 flex-shrink-0"
                      style={{ background: session.color }}
                    />
                    <CardContent className="p-5 flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <Badge
                          className="border-0 text-[10px] font-bold"
                          style={{
                            background: `${session.color}12`,
                            color: session.color,
                          }}
                        >
                          {session.id}
                        </Badge>
                        <span className="text-[11px] font-medium text-slate-400">
                          {t(session.weekKey)}
                        </span>
                      </div>
                      <h3 className="text-sm font-bold mb-1" style={{ color: COLORS.ink }}>
                        {t(session.titleKey)}
                      </h3>
                      <p className="text-xs leading-relaxed text-slate-500">{t(session.descKey)}</p>
                      <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-400">
                        <span className="flex items-center gap-1">
                          <PlayCircle className="h-3 w-3" />
                          {t('landing.parcours.video')}
                        </span>
                        <span className="flex items-center gap-1">
                          <ClipboardCheck className="h-3 w-3" />
                          {t('landing.parcours.quiz')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Rocket className="h-3 w-3" />
                          {t('landing.parcours.livrable')}
                        </span>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ───────────────────────────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-20">
          <motion.div {...fadeUp} className="text-center mb-14">
            <Badge
              className="mb-4 border-0"
              style={{ background: `${COLORS.blue}12`, color: COLORS.blue }}
            >
              {t('landing.testimonials.badge')}
            </Badge>
            <h2 className="text-3xl font-bold md:text-4xl" style={{ color: COLORS.ink }}>
              {t('landing.testimonials.title')}
            </h2>
            <p className="mt-3 mx-auto max-w-lg text-slate-500">
              {t('landing.testimonials.subtitle')}
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TESTIMONIAL_ITEMS.map(({ name, roleKey, quoteKey, avatar }, i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
              >
                <Card className="h-full border-slate-200/80 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <Quote className="h-8 w-8 mb-4 flex-shrink-0" style={{ color: `${COLORS.orange}40` }} />
                    <p className="text-sm leading-relaxed text-slate-600 flex-1">
                      &ldquo;{t(quoteKey)}&rdquo;
                    </p>
                    <div className="mt-6 flex items-center gap-3 pt-4 border-t border-slate-100">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0"
                        style={{ background: `${COLORS.blue}10` }}
                      >
                        <img
                          src={avatar}
                          alt={name}
                          className="h-6 w-6 rounded-full"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-bold" style={{ color: COLORS.ink }}>
                          {name}
                        </div>
                        <div className="text-xs text-slate-400">{t(roleKey)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ──────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${COLORS.blue} 0%, #0E4D78 100%)`,
        }}
      >
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-[0.06]"
          style={{ background: COLORS.orange, transform: 'translate(40%, -50%)' }}
        />

        <div className="relative mx-auto max-w-3xl px-4 py-16 md:py-20 text-center">
          <motion.div {...fadeUp}>
            <div
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl"
              style={{ background: COLORS.orange }}
            >
              <Sparkles className="h-8 w-8 text-white" />
            </div>

            <h2 className="text-3xl font-bold text-white md:text-4xl">
              {t('landing.cta_final.title')}
            </h2>
            <p className="mt-4 mx-auto max-w-md text-white/60">
              {t('landing.cta_final.subtitle')}
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button
                variant="primary"
                size="lg"
                onClick={onStart}
                className="group"
                style={{ background: COLORS.orange, borderColor: COLORS.orange }}
              >
                {t('landing.cta_final.se_connecter')}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>

            <p className="mt-6 text-xs text-white/30">
              {t('landing.cta_final.acces_reserve')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── BRAND FOOTER ───────────────────────────────────── */}
      <BrandFooter />
    </div>
  );
}
